import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { Provider } from 'launchdarkly-react-client-sdk/lib/context';
import { createContext, ReactNode } from 'react';

import config from '../config';

const LAUNCHDARKLY_CLIENT_SIDE_ID = config().launchdarkly_client_side_id;

export const MockLaunchDarklyContext = createContext(false);

// for development purposes only
const mockedFlags = {
  deliveryServiceMetrobiEligibility: false,
  deliveryServiceShowRouteImporter: ['*'],
  deliveryServiceShowAdditionalTasks: ['*'] as string[],
  deliveryServiceShowAnalyticsPage: true,
  deliveryServiceSeparatePrimaryStatusState: true,
  deliveryServiceFulfillmentDeltasPage: true,
  deliveryServiceShowPackingListPage: true,
  deliveryServiceDriverSafetyAcknowledgementsPage: false,
  deliveryServiceVehicleInspectionReportsPage: false,
  deliveryServiceUnlockRoutingImport: false,
  deliveryServiceDriverPerformancePage: false,
  deliveryServiceShowWarehouseDeliveryDays: true,
  deliveryServiceSeparateBulkAmbientPackingGroups: false,
  deliveryServiceVendorReceivingPage: false,
  deliveryServicePutawayReportPage: false,
  deliveryServicePutawayApp: [] as string[],
  // Enabled locally so the mocked stack can still submit movements; the real
  // flag defaults to off, which keeps the write disabled until a warehouse is
  // explicitly rolled out.
  deliveryServiceConfirmPutawayPallet: ['*'] as string[],
  // On locally so replenishment can be built against the mocked stack. The
  // real flag defaults to off.
  deliveryServicePutawayCompanionReplenishment: ['*'] as string[],
  // On locally so the manual transfer flow can be built against the mocked
  // stack. The real flag defaults to an empty warehouse list.
  deliveryServicePutawayAppManualTransfer: ['*'] as string[],
  deliveryServiceOptimizationScenarioComparison: true,
  deliveryServiceOptimizationScenarioManagement: true,
  deliveryServiceOptimizationApplyRoutingConfiguration: true,
  deliveryServiceOptimizationDefineFleet: ['*'] as string[],
  deliveryServiceOptimizationFleetCompositionReadOnly: true,
  deliveryServiceInvoiceByDeliveredQty: [] as string[],
  deliveryServiceFilterDriversByPayrollId: [] as string[],
  deliveryServiceRoutificEtasCalculation: ['*'] as string[],
  deliveryServiceRoutificResequence: ['*'] as string[],
  deliveryServiceProcessPathWedgeScan: true,
  deliveryServiceShowRfsmartCredentialAdmin: true,
  deliveryServiceBadgeRevoke: true,
  deliveryServiceOutboundGates: ['*'] as string[],
  deliveryServiceDeliveryOverridesPage: false,
  deliveryServiceDeliveryOverridesCopy: false,
  deliveryServiceDockManagerPage: true,
  deliveryServiceDockBookingStandingAppointments: true,
};

type MockedFlags = typeof mockedFlags;
type MockedFlagOverrides = Partial<MockedFlags>;

const getMockedLDFlags = (): MockedFlags => {
  const windowWithOverrides = window as Window & {
    __LD_FLAG_OVERRIDES__?: MockedFlagOverrides;
  };

  return {
    ...mockedFlags,
    ...(windowWithOverrides.__LD_FLAG_OVERRIDES__ ?? {}),
  };
};

const mockedLDProvider = ({
  children,
}: {
  children: ReactNode;
}): JSX.Element => (
  <MockLaunchDarklyContext.Provider value>
    <Provider
      value={{
        flags: getMockedLDFlags(),
        ldClient: undefined,
        flagKeyMap: {},
      }}
    >
      {children}
    </Provider>
  </MockLaunchDarklyContext.Provider>
);

export const initLDProvider = (): Promise<
  ({ children }: { children: ReactNode }) => JSX.Element
> => {
  if (!LAUNCHDARKLY_CLIENT_SIDE_ID) {
    return Promise.resolve(mockedLDProvider);
  }
  return asyncWithLDProvider({ clientSideID: LAUNCHDARKLY_CLIENT_SIDE_ID });
};
